import { useEffect, useState } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import {
  ArrowRight, ArrowUpRight, Linkedin, Calendar, Globe, MapPin,
  Briefcase, Mail, BarChart3, Smartphone, Eye, MousePointer, Check, ChevronDown,
} from "lucide-react";
import Button from "../ui/Button";
import HeroHandlePicker from "../HeroHandlePicker";

// Variant B — Product/bento dense.
// Linear/Stripe-style. Lots of mockup UI, dense bento grids, mix of dark
// and light sections, info-rich. Designed to convince by showing.

const FAQ = [
  { q: "Do I need an app?", a: "No. The recipient opens a normal URL." },
  { q: "What if I change jobs?", a: "Edit the card. The link stays the same." },
  { q: "Free tier?", a: "Yes — 1 card, 5 links, basic analytics, forever." },
  { q: "How does the NFC card work?", a: "Hold it near a phone. The card opens. Has a QR backup." },
];

export default function LandingProduct() {
  const { isSignedIn } = useUser();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    document.title = "WhoBrew — Virtual business cards, with proof";
  }, []);

  return (
    <div className="bg-white text-coffee-900">
      {/* HERO — split, info-dense */}
      <section className="relative border-b border-coffee-100">
        <div className="absolute inset-0 bg-dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-eyebrow font-semibold text-coffee-500 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live · always points to your latest
              </div>
              <h1 className="text-display text-[44px] sm:text-[64px] md:text-[76px] leading-[1] tracking-[-0.02em]">
                Your business card,
                <br />
                <span className="text-coffee-700">with receipts.</span>
              </h1>
              <p className="mt-5 text-lg text-coffee-700 max-w-lg">
                The link that always points to your latest. Track every visit. Update every share at once.
              </p>
              <div className="mt-7 max-w-xl">
                <HeroHandlePicker origin={typeof window !== "undefined" ? window.location.origin : "https://whobrew.us"} />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-coffee-500">
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> Free forever tier</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> Cancel anytime</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> No app required</span>
              </div>
            </div>

            {/* Compact preview cluster */}
            <div className="lg:col-span-5">
              <div className="relative">
                <PreviewCard />
                {/* Visit counter chip */}
                <div className="absolute -top-3 -right-3 bg-coffee-900 text-white px-3 py-1.5 rounded-full text-xs font-mono shadow-coffee-lg">
                  342 visits · example
                </div>
                {/* Mini stats card */}
                <div className="hidden sm:block absolute -bottom-6 -left-6 bg-white border border-coffee-100 rounded-xl shadow-coffee-lg p-3 w-44">
                  <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-1.5">Top link today</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-coffee-700" />
                    <span className="text-sm text-coffee-900 font-medium">Calendly</span>
                    <span className="ml-auto text-xs font-mono text-coffee-500">87</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE OF LINK TYPES (honest — these are real link presets) */}
      <section className="py-8 border-b border-coffee-100 overflow-hidden">
        <p className="text-center text-[11px] tracking-eyebrow font-semibold text-coffee-500 mb-5">
          Plug in any link people already ask you for
        </p>
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 max-w-5xl mx-auto px-4">
          {["LinkedIn", "Calendly", "Cal.com", "Stripe Payment", "Portfolio", "Resume PDF", "GitHub", "YouTube", "Newsletter", "Substack", "Spotify", "Open roles", "Zillow profile", "Matterport tour", "Apple Wallet"].map((t) => (
            <span key={t} className="text-xs px-3 py-1.5 rounded-full bg-coffee-50 border border-coffee-100 text-coffee-700">
              {t}
            </span>
          ))}
        </div>
      </section>

      {/* BENTO 1 — feature grid with embedded UI */}
      <section className="py-20 sm:py-28 bg-coffee-50 border-b border-coffee-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">Why teams switch</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">
              Show up, see who showed up.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
            {/* Big tile — analytics mock */}
            <div className="lg:col-span-7 lg:row-span-2 bg-coffee-900 text-white rounded-2xl p-7 sm:p-9 overflow-hidden relative min-h-[380px]">
              <div className="absolute -top-32 -right-24 w-72 h-72 rounded-full bg-coffee-700 blur-3xl opacity-50" />
              <div className="relative">
                <div className="eyebrow text-coffee-200 mb-3">Analytics</div>
                <h3 className="text-display text-2xl sm:text-3xl text-white">Visit analytics, free with every plan.</h3>
                <p className="mt-3 text-coffee-200 max-w-sm">No third-party tracker. Per-link clicks, devices, geography.</p>

                {/* Mock analytics grid */}
                <div className="mt-7 grid grid-cols-3 gap-2.5">
                  {[
                    { label: "Visits", val: "1,284", trend: "+18%" },
                    { label: "Unique", val: "892", trend: "+12%" },
                    { label: "Saved", val: "147", trend: "+24%" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-3.5">
                      <p className="text-[10px] tracking-eyebrow text-coffee-200 font-semibold">{s.label}</p>
                      <p className="text-2xl font-mono text-white mt-1">{s.val}</p>
                      <p className="text-[10px] text-emerald-400 mt-0.5">{s.trend}</p>
                    </div>
                  ))}
                </div>

                {/* Mock chart */}
                <div className="mt-5 h-24 flex items-end gap-1.5">
                  {[28, 32, 24, 48, 36, 64, 56, 80, 72, 92, 84, 100, 88, 96].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-coffee-700 to-coffee-500" style={{ height: `${h}%` }} />
                  ))}
                </div>

                {/* Mock link rows */}
                <div className="mt-6 space-y-2">
                  {[
                    { icon: <Calendar className="w-4 h-4" />, title: "Book a meeting", clicks: 87 },
                    { icon: <Linkedin className="w-4 h-4" />, title: "LinkedIn", clicks: 64 },
                    { icon: <Globe className="w-4 h-4" />, title: "Portfolio", clicks: 41 },
                  ].map((l) => (
                    <div key={l.title} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                      <span className="text-coffee-200">{l.icon}</span>
                      <span className="text-sm text-white flex-1">{l.title}</span>
                      <span className="text-xs font-mono text-coffee-200">{l.clicks}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[10px] font-mono text-coffee-300">Example data — illustrative.</p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white border border-coffee-100 rounded-2xl p-7 hover-lift">
              <Eye className="w-5 h-5 text-coffee-700 mb-3" />
              <h3 className="text-display text-xl text-coffee-900">Visit visibility</h3>
              <p className="mt-2 text-sm text-coffee-600 leading-relaxed">
                See when your card gets viewed. Per-link click counts. Devices. Geography. No
                cookies, no third-party tracker.
              </p>
            </div>
            <div className="lg:col-span-5 bg-white border border-coffee-100 rounded-2xl p-7 hover-lift">
              <Smartphone className="w-5 h-5 text-coffee-700 mb-3" />
              <h3 className="text-display text-xl text-coffee-900">NFC, no app</h3>
              <p className="mt-2 text-sm text-coffee-600 leading-relaxed">
                Order a thin PVC card for $14.99. Tap to any modern phone — iPhone 7+ or any Android
                from the last 8 years. Printed QR for everyone else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BIG SCREENSHOT-STYLE BLOCK */}
      <section className="py-20 sm:py-28 border-b border-coffee-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="eyebrow mb-3">Dashboard</div>
              <h2 className="text-display text-3xl sm:text-5xl text-coffee-900 leading-[1.05]">
                Your card. Your team's cards. One screen.
              </h2>
              <p className="mt-5 text-lg text-coffee-700 max-w-md">
                Reorder links. Swap themes. See which event drove visits. Bulk-order physical
                cards. All from the same dashboard.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Up to 3 cards per Pro account — one per role",
                  "11 themes with custom colors",
                  "CSV export for leads + analytics",
                  "Lead capture from public card forms",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-coffee-700">
                    <Check className="w-4 h-4 text-coffee-700 mt-0.5 flex-shrink-0" strokeWidth={3} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl border border-coffee-200 bg-coffee-50 p-2 shadow-coffee-lg">
                {/* Window chrome */}
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="ml-3 text-[10px] font-mono text-coffee-400">whobrew.us/my-card</span>
                </div>
                {/* Inner mock */}
                <div className="bg-white rounded-xl border border-coffee-100 p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-coffee-700 flex items-center justify-center text-white text-sm font-bold">ST</div>
                    <div>
                      <p className="text-sm font-semibold text-coffee-900">Sean Thomas</p>
                      <p className="text-[11px] font-mono text-coffee-500">Founder · WhoBrew</p>
                    </div>
                    <span className="ml-auto text-[10px] tracking-eyebrow font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Active</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ k: "Visits", v: "1,284" }, { k: "Clicks", v: "603" }, { k: "Saved", v: "147" }].map(s => (
                      <div key={s.k} className="rounded-lg bg-coffee-50 border border-coffee-100 p-2.5">
                        <p className="text-[9px] tracking-eyebrow font-semibold text-coffee-500">{s.k}</p>
                        <p className="text-base font-mono text-coffee-900">{s.v}</p>
                      </div>
                    ))}
                  </div>
                  {[
                    { i: <Linkedin className="w-3.5 h-3.5" />, t: "LinkedIn", c: 64 },
                    { i: <Calendar className="w-3.5 h-3.5" />, t: "Book a meeting", c: 87 },
                    { i: <Globe className="w-3.5 h-3.5" />, t: "Portfolio", c: 41 },
                    { i: <Briefcase className="w-3.5 h-3.5" />, t: "Open roles", c: 22 },
                  ].map((l) => (
                    <div key={l.t} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-coffee-50 border border-coffee-100">
                      <span className="w-7 h-7 rounded-md bg-white text-coffee-700 flex items-center justify-center border border-coffee-100">{l.i}</span>
                      <span className="text-xs text-coffee-900 flex-1">{l.t}</span>
                      <span className="text-[10px] font-mono text-coffee-500">{l.c} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES BENTO */}
      <section className="py-20 sm:py-28 border-b border-coffee-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">Built for the work</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">
              Pick your role. We do the setup.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { tag: "Recruiters", body: "Career fairs. Open roles. Candidate intake.", to: "/for/recruiters", icon: <Briefcase className="w-4 h-4" /> },
              { tag: "Sales", body: "Demos. Decks. Lead capture.", to: "/for/sales", icon: <BarChart3 className="w-4 h-4" /> },
              { tag: "Real estate", body: "Listings. Tours. Sign-in QR.", to: "/for/real-estate", icon: <MapPin className="w-4 h-4" /> },
              { tag: "Creators", body: "Newsletter. Merch. Podcast.", to: "/how-it-works", icon: <Mail className="w-4 h-4" /> },
            ].map((c) => (
              <a key={c.tag} href={c.to} className="group bg-white border border-coffee-100 rounded-2xl p-5 hover-lift block">
                <div className="w-9 h-9 rounded-lg bg-coffee-50 border border-coffee-100 flex items-center justify-center mb-3 text-coffee-700">
                  {c.icon}
                </div>
                <h3 className="text-coffee-900 font-semibold text-sm">{c.tag}</h3>
                <p className="mt-1 text-xs text-coffee-600 leading-relaxed">{c.body}</p>
                <span className="mt-3 inline-flex items-center text-[11px] font-medium text-coffee-700 group-hover:gap-1 transition-all">
                  Open <ArrowUpRight className="w-3 h-3 ml-1" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT -> LEAD FLOW */}
      <section className="py-20 sm:py-28 bg-coffee-50 border-b border-coffee-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">From visit to lead</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">
              Every visit is a lead, if you want it.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { icon: <MousePointer className="w-4 h-4" />, n: "01", title: "Open the link", body: "Recipient opens your card via QR, link, or NFC tap." },
              { icon: <Eye className="w-4 h-4" />, n: "02", title: "Browse + connect", body: "They tap Contact me, write you a note, send their info." },
              { icon: <BarChart3 className="w-4 h-4" />, n: "03", title: "Lands in Leads", body: "Their info, their message, source-tagged 'via card'." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-coffee-100 rounded-2xl p-6 hover-lift">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-coffee-700">{s.icon}</span>
                  <span className="text-[11px] font-mono text-coffee-500">{s.n}</span>
                </div>
                <h3 className="font-semibold text-coffee-900">{s.title}</h3>
                <p className="mt-1 text-sm text-coffee-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 sm:py-28 border-b border-coffee-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { name: "Starter", price: "Free", note: "1 card · 5 links · basic analytics" },
              { name: "Pro", price: "$4.99/mo", note: "3 cards · 10 links · click tracking", highlight: true },
              { name: "Physical NFC", price: "$14.99", note: "PVC card with chip + QR backup" },
            ].map((p) => (
              <div key={p.name} className={`rounded-2xl p-7 ${p.highlight ? "bg-coffee-900 text-white ring-2 ring-coffee-700" : "bg-white border border-coffee-100"}`}>
                <p className={`text-[11px] tracking-eyebrow font-semibold mb-2 ${p.highlight ? "text-coffee-200" : "text-coffee-500"}`}>{p.name}</p>
                <p className={`text-3xl font-bold ${p.highlight ? "text-white" : "text-coffee-900"}`}>{p.price}</p>
                <p className={`text-sm mt-3 ${p.highlight ? "text-coffee-200" : "text-coffee-600"}`}>{p.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="text-display text-3xl sm:text-5xl text-coffee-900 mb-8">Common questions.</h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i} className="bg-white border border-coffee-100 rounded-xl">
                  <button onClick={() => setOpenFAQ(open ? null : i)} className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-coffee-50">
                    <span className="font-semibold text-coffee-900">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-coffee-500 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="px-5 pb-4 text-sm text-coffee-700 leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="py-24 sm:py-32 bg-coffee-900 border-t border-coffee-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-display text-4xl sm:text-6xl text-white">Build it in a minute.</h2>
          <p className="mt-4 text-coffee-200 text-lg">Free. No credit card. Live the moment you save.</p>
          <div className="mt-8 flex justify-center">
            {isSignedIn ? (
              <Button to="/my-card" variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Open my card</Button>
            ) : (
              <SignUpButton mode="modal">
                <Button variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Claim my link</Button>
              </SignUpButton>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* Embedded preview card */
function PreviewCard() {
  return (
    <div className="bg-white border border-coffee-100 rounded-2xl shadow-coffee-lg overflow-hidden max-w-sm mx-auto">
      <div className="bg-gradient-to-br from-coffee-700 to-coffee-900 h-20" />
      <div className="text-center -mt-9 px-5">
        <div className="w-16 h-16 rounded-full bg-coffee-700 flex items-center justify-center text-white font-bold ring-4 ring-white shadow mx-auto">ST</div>
        <h3 className="mt-2 text-base font-bold text-coffee-900">Sean Thomas</h3>
        <p className="text-xs text-coffee-600">Founder · WhoBrew</p>
      </div>
      <div className="px-4 pb-4 mt-3 space-y-1.5">
        {[
          { i: <Linkedin className="w-3.5 h-3.5" />, t: "LinkedIn" },
          { i: <Calendar className="w-3.5 h-3.5" />, t: "Book a meeting" },
          { i: <Globe className="w-3.5 h-3.5" />, t: "Portfolio" },
        ].map((l) => (
          <div key={l.t} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-coffee-50 border border-coffee-100">
            <span className="w-6 h-6 rounded-md bg-white text-coffee-700 flex items-center justify-center border border-coffee-100">{l.i}</span>
            <span className="text-xs text-coffee-900 font-medium">{l.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
